import Modal from 'antd/es/modal';
import Popover from 'antd/es/popover';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import userDefaultAvatar from '../../../../assets/img/placeholder-user.png';
import { ReactComponent as Alert } from '../../../../assets/svg/icons/alert.svg';
import { ReactComponent as Chevron } from '../../../../assets/svg/icons/chevron-down.svg';
import { getCommonRoutePath, onUserImgError } from '../../../../helpers/Helpers';
import ParticipantsInfoPopup from '../../components/popups/ParticipantsInfoPopup';

import AmbassadorProfilePopup from '../popups/AmbassadorProfilePopup';

import { Button, Image } from 'antd';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

const ProspectMessagingHeader = ({ handleSurePopupClick, handleShowOptionsClickOnMobile, showOptions }) => {
    const navigate = useNavigate();

    const { isMobile } = useSelector((state) => state.widget);
    const currentChat = useSelector((state) => state.currentChat);

    const [documentWidth, setDocumentWidth] = useState(document.body.clientWidth || 1100);
    const [membersPerRow, setMembersPerRow] = useState(
        (document.body.clientWidth || 1100) < 750 ? 1 : Math.ceil(documentWidth / 2 / 230)
    );
    const [scrollPosition, setScrollPosition] = useState(0);
    const [showParticipantsModal, setShowParticipantsModal] = useState(false);

    useEffect(() => {
        const documentCurrentWidth = document.body.clientWidth || 1100;

        if (documentCurrentWidth !== documentWidth) {
            setDocumentWidth(documentCurrentWidth);
            setMembersPerRow(documentCurrentWidth < 750 ? 1 : Math.ceil(documentCurrentWidth / 2 / 230));
        }
    }, [documentWidth, document.body.clientWidth]);

    const goToStudent = useCallback(
        (student) => {
            navigate(
                `${getCommonRoutePath()}/chat-to-us/${
                    student.role_key === 'univercity-student' ? student.id : 'university'
                }`
            );
        },
        [navigate]
    );

    const scrollMembers = useCallback(
        (scrollTop) => {
            const saCount = currentChat.data && currentChat.data.ambassadors ? currentChat.data.ambassadors.length : 0;

            if (
                (scrollPosition < 0 && !scrollTop) ||
                (Math.abs(scrollPosition) + 1 < Math.ceil(saCount / membersPerRow) && scrollTop)
            ) {
                setScrollPosition(scrollTop ? scrollPosition - 1 : scrollPosition + 1);
            }
        },
        [membersPerRow, scrollPosition, currentChat]
    );

    const membersView = useMemo(() => {
        const commonRoutePath = getCommonRoutePath();
        const currentChatInfo = currentChat.data;

        let membersViewArr = [];
        if (currentChatInfo.ambassadors) {
            membersViewArr = currentChatInfo.ambassadors.map((member, index) => {
                member.name = member.name.split(' ')[0];
                const online = member.last_online
                    ? dayjs.duration(dayjs().diff(dayjs(member.last_online.updated_at))).asMinutes()
                    : 10;

                let lastOnline = null;
                if (online >= 5 && member.last_online && member.last_online.updated_at) {
                    // lastOnline = dayjs.utc(member.last_online.updated_at.toLocaleString()).fromNow(); // not correct with more than text
                    try {
                        const nowDate = dayjs();
                        const onlineDate = dayjs(member.last_online.updated_at);

                        const days = nowDate.diff(onlineDate, 'days');
                        lastOnline = days ? '1 day' : '';
                        if (!days) {
                            const mins = nowDate.diff(onlineDate, 'minutes', true);
                            const totalHours = parseInt(mins / 60);
                            const totalMins = dayjs().minute(mins).$m;

                            lastOnline += totalHours ? totalHours + ' hours ' : '';
                            lastOnline += totalMins ? totalMins + ' minutes ' : '';
                        }
                        lastOnline += ' ago';
                    } catch (err) {
                        console.log(err);
                    }
                }

                return (
                    <div key={member.id} className="chat-name-head-row">
                        <div className="chat-name-img-container">
                            <Button
                                className="chat-name-img-block ambassador-chat-image"
                                onClick={() => goToStudent(member)}
                            >
                                <Image
                                    className="chat-name-img"
                                    src={
                                        member.avatar
                                            ? member.avatar.sizes['70x70'] || member.avatar.original
                                            : userDefaultAvatar
                                    }
                                    onError={onUserImgError}
                                    alt={member.name ? member.name.slice(0, 1).toUpperCase() : 'D'}
                                />
                            </Button>
                            {lastOnline ? (
                                <Popover
                                    placement="bottomLeft"
                                    content={<div>Last online more than {lastOnline}</div>}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <span className={online < 5 ? 'mod-online' : 'mod-offline'}></span>
                                </Popover>
                            ) : (
                                <span className={online < 5 ? 'mod-online' : 'mod-offline'}></span>
                            )}
                        </div>

                        <div className="chat-name-block">
                            <NavLink
                                end
                                to={
                                    commonRoutePath +
                                    '/chat-to-us/' +
                                    (member.role_key === 'univercity-student' ? member.id : 'university')
                                }
                                className="chat-name ambassador-chat-name-header ambassador-chat-name-header-shortened"
                            >
                                {member.name}
                            </NavLink>
                            <Popover
                                placement="bottomLeft"
                                overlayStyle={{ width: 390 }}
                                content={
                                    <AmbassadorProfilePopup
                                        member={member}
                                        commonRoutePath={commonRoutePath}
                                        online={online}
                                        goToStudent={goToStudent}
                                    />
                                }
                                onClick={(e) => e.preventDefault()}
                            >
                                <p className="chat-member-skills chat-member-skills-shortened">
                                    {member.user_tags.courses_types ? member.user_tags.courses_types[0].name + ' ' : ''}
                                    {member.user_tags.subject ? member.user_tags.subject[0].name + ' ' : ''}
                                    {member.user_tags.job_role ? member.user_tags.job_role[0].name + ' ' : ''}
                                    {member.user_tags.industry ? member.user_tags.industry[0].name : ''}
                                    {member.user_tags.staff ? member.user_tags.staff[0].name + ' Staff' : ''}
                                    {member.children && member.children[0]
                                        ? 'Parent of ' +
                                          member.children.length +
                                          ' children at ' +
                                          currentChatInfo.university.name
                                        : ''}
                                    {member.user_tags &&
                                    member.user_tags.profile &&
                                    member.user_tags.profile[0] &&
                                    member.user_tags.profile[0].key === '_school_student'
                                        ? 'Student'
                                        : ''}
                                </p>
                            </Popover>
                        </div>
                    </div>
                );
            });
        }
        return membersViewArr;
    }, [currentChat]);

    const membersViewMobile = useMemo(() => {
        const commonRoutePath = getCommonRoutePath();
        const currentChatInfo = currentChat.data;

        let membersViewMobileArr = [];
        if (currentChatInfo.ambassadors && currentChatInfo.ambassadors.length > 0) {
            if (currentChatInfo.ambassadors.length === 1) {
                membersViewMobileArr = currentChatInfo.ambassadors.map((member, index, arr) => {
                    const online = member.last_online
                        ? dayjs.duration(dayjs().diff(dayjs(member.last_online.updated_at))).asMinutes()
                        : 10;
                    return (
                        <div key={member.id} className="chat-name-head-row">
                            <div className="chat-name-img-container">
                                <Button
                                    className="chat-name-img-block ambassador-chat-image"
                                    onClick={() => goToStudent(member)}
                                >
                                    <Image
                                        className="chat-name-img"
                                        src={
                                            member.avatar
                                                ? member.avatar.sizes['70x70'] || member.avatar.original
                                                : userDefaultAvatar
                                        }
                                        onError={onUserImgError}
                                        alt={member.name ? member.name.slice(0, 1).toUpperCase() : 'D'}
                                    />
                                </Button>
                                <span className={online < 5 ? 'mod-online' : 'mod-offline'}></span>
                            </div>

                            <div className="chat-name-block">
                                <NavLink
                                    end
                                    to={
                                        commonRoutePath +
                                        '/chat-to-us/' +
                                        (member.role_key === 'univercity-student' ? member.id : 'university')
                                    }
                                    className="chat-name ambassador-chat-name-header ambassador-chat-name-header-shortened"
                                >
                                    {member.name}
                                </NavLink>
                                <p className="chat-member-skills chat-member-skills-shortened">
                                    {member.user_tags.courses_types ? member.user_tags.courses_types[0].name + ' ' : ''}
                                    {member.user_tags.subject ? member.user_tags.subject[0].name + ' ' : ''}
                                    {member.user_tags.job_role ? member.user_tags.job_role[0].name + ' ' : ''}
                                    {member.user_tags.industry ? member.user_tags.industry[0].name : ''}
                                    {member.user_tags.staff ? member.user_tags.staff[0].name + ' Staff' : ''}
                                    {member.children && member.children[0]
                                        ? 'Parent of ' +
                                          member.children.length +
                                          ' children at ' +
                                          currentChatInfo.university.name
                                        : ''}
                                    {member.user_tags &&
                                    member.user_tags.profile &&
                                    member.user_tags.profile[0] &&
                                    member.user_tags.profile[0].key === '_school_student'
                                        ? 'Student'
                                        : ''}
                                </p>
                            </div>
                        </div>
                    );
                });
            } else {
                const member = currentChatInfo.ambassadors[0];
                membersViewMobileArr = [
                    <div className="chat-name-block" key={member.id}>
                        <p>
                            {member.name} (
                            {member.user_tags.courses_types ? member.user_tags.courses_types[0].name + ' ' : ''}
                            {member.user_tags.subject ? member.user_tags.subject[0].name + ' ' : ''}
                            {member.user_tags.job_role ? member.user_tags.job_role[0].name + ' ' : ''}
                            {member.user_tags.industry ? member.user_tags.industry[0].name : ''}
                            {member.user_tags.staff ? member.user_tags.staff[0].name + ' Staff' : ''}
                            {member.children && member.children[0]
                                ? 'Parent of ' +
                                  member.children.length +
                                  ' children at ' +
                                  currentChatInfo.university.name
                                : ''}
                            {member.user_tags &&
                            member.user_tags.profile &&
                            member.user_tags.profile[0] &&
                            member.user_tags.profile[0].key === '_school_student'
                                ? 'Student'
                                : ''}
                            )
                        </p>
                        <Button className="participants-btn" onClick={onShowParticipants}>
                            +{currentChatInfo.ambassadors.length - 1} more Participants
                        </Button>
                    </div>,
                ];
            }
        }
        return membersViewMobileArr;
    }, [currentChat]);

    const onShowParticipants = useCallback(() => {
        setShowParticipantsModal(true);
    }, []);

    const handleCloseParticipantsInfoPopup = useCallback(() => {
        setShowParticipantsModal(false);
    }, []);

    const currentChatInfo = currentChat.data;
    const saCount = currentChat.data && currentChat.data.ambassadors ? currentChat.data.ambassadors.length : 0;
    const membersPerRowShow = saCount < membersPerRow ? saCount : membersPerRow;
    const transition = scrollPosition * 69;
    const isTopChevronActive = scrollPosition < 0;
    const isBottomChevronActive = Math.abs(scrollPosition) + 1 < Math.ceil(saCount / membersPerRowShow);

    const members = isMobile ? membersViewMobile : membersView;

    const flagModalChild = (
        <div className={`sub-title ${isMobile ? 'mobile' : ''}`}>
            If you report this conversation, it will be reviewed by an administrator at{' '}
            {currentChat.university ? currentChat.university.name : 'institution'}.
        </div>
    );
    const closeModalChild = (
        <div className="sub-title">
            By closing this conversation, you will no longer be able to chat with{' '}
            {currentChatInfo.members && currentChatInfo.members[0] ? currentChatInfo.members[0].name : 'Deleted user'}.
        </div>
    );
    return (
        <div className="prospect-messaging-header">
            <div
                className="chat-head-left"
                style={{
                    gridTemplateColumns: 'repeat(' + membersPerRowShow + ', 1fr)',
                    transform: 'translate(0, ' + transition + 'px)',
                }}
            >
                {members.length > 0 ? (
                    members
                ) : (
                    <div className="chat-name-head-row">
                        <div className="chat-name-block">
                            <h3 className="chat-name">Deleted user</h3>
                        </div>
                    </div>
                )}
            </div>
            {!isMobile && (isTopChevronActive || isBottomChevronActive) && (
                <div className="members-scroll-block">
                    <Chevron
                        className={'chevron-top' + (isTopChevronActive ? ' active-chevron' : '')}
                        onClick={() => scrollMembers(false)}
                    />
                    <Chevron
                        className={isBottomChevronActive ? 'active-chevron' : ''}
                        onClick={() => scrollMembers(true)}
                    />
                </div>
            )}
            {isMobile && currentChat.userMessagesExist ? (
                <Button
                    htmlType="button"
                    className={'show-options-button' + (showOptions ? ' pressed' : '')}
                    aria-label="Show options"
                    title="Show options"
                    onClick={handleShowOptionsClickOnMobile}
                >
                    <svg id="Layer_1" height="20" viewBox="0 0 512 512" width="20" xmlns="http://www.w3.org/2000/svg">
                        <path d="m464.883 64.267h-417.766c-25.98 0-47.117 21.136-47.117 47.149 0 25.98 21.137 47.117 47.117 47.117h417.766c25.98 0 47.117-21.137 47.117-47.117 0-26.013-21.137-47.149-47.117-47.149z" />
                        <path d="m464.883 208.867h-417.766c-25.98 0-47.117 21.136-47.117 47.149 0 25.98 21.137 47.117 47.117 47.117h417.766c25.98 0 47.117-21.137 47.117-47.117 0-26.013-21.137-47.149-47.117-47.149z" />
                        <path d="m464.883 353.467h-417.766c-25.98 0-47.117 21.137-47.117 47.149 0 25.98 21.137 47.117 47.117 47.117h417.766c25.98 0 47.117-21.137 47.117-47.117 0-26.012-21.137-47.149-47.117-47.149z" />
                    </svg>
                </Button>
            ) : null}
            {!currentChatInfo.isMyAccountBlocked ? (
                <>
                    {isMobile ? (
                        <div className={'chat-head-right mobile' + (showOptions ? ' show-options' : '')}>
                            <Button
                                htmlType="button"
                                disabled={
                                    !currentChat.userMessagesExist ||
                                    currentChatInfo.user_reported ||
                                    currentChatInfo.reported
                                }
                                className="chat-control"
                                aria-label="Report a conversation"
                                title="Report a conversation"
                                onClick={() => {
                                    handleSurePopupClick('handleFlagClick', flagModalChild, true);
                                }}
                            >
                                <div className="option-text">
                                    {currentChatInfo.user_reported || currentChatInfo.reported
                                        ? 'Conversation is reported'
                                        : 'Report a conversation'}
                                </div>
                            </Button>
                            <div className="popup-separator" />

                            {!currentChatInfo.closed && (
                                <Button
                                    htmlType="button"
                                    disabled={!currentChat.userMessagesExist}
                                    className="chat-control chat-close"
                                    title="Close"
                                    aria-label="Close this conversation"
                                    onClick={() => {
                                        handleSurePopupClick('closeDialog', closeModalChild);
                                    }}
                                >
                                    <div className="option-text">Close chat</div>
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className={'chat-head-right' + (showOptions ? ' show-options' : '')}>
                            <Popover
                                placement="bottomLeft"
                                overlayClassName="head-right-popup"
                                content={
                                    currentChatInfo.user_reported || currentChatInfo.reported ? (
                                        <span className="toggle-left toggle-left--flagged">
                                            Conversation is reported
                                        </span>
                                    ) : (
                                        <span className="toggle-left">Report a conversation</span>
                                    )
                                }
                                onClick={(e) => e.preventDefault()}
                            >
                                <Button
                                    htmlType="button"
                                    disabled={
                                        !currentChat.userMessagesExist ||
                                        currentChatInfo.user_reported ||
                                        currentChatInfo.reported
                                    }
                                    className="chat-control"
                                    aria-label="Report a conversation"
                                    title=""
                                    onClick={() => {
                                        handleSurePopupClick('handleFlagClick', flagModalChild, true);
                                    }}
                                >
                                    <Alert />
                                </Button>
                            </Popover>

                            {!currentChatInfo.closed && (
                                <Popover
                                    placement="bottomLeft"
                                    overlayClassName="head-right-popup"
                                    content={<span className="toggle-left">Close conversation</span>}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <Button
                                        htmlType="button"
                                        disabled={!currentChat.userMessagesExist}
                                        className="chat-control chat-close"
                                        title=""
                                        aria-label="Close this conversation"
                                        onClick={() => {
                                            handleSurePopupClick('closeDialog', closeModalChild);
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="-2 -2 24 24"
                                            width="32"
                                            height="32"
                                            preserveAspectRatio="xMinYMin"
                                            className="jam jam-close"
                                        >
                                            <path d="M5.094 16.32A8 8 0 0 0 16.32 5.094L5.094 16.32zM3.68 14.906L14.906 3.68A8 8 0 0 0 3.68 14.906zM10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z" />
                                        </svg>
                                    </Button>
                                </Popover>
                            )}
                        </div>
                    )}
                </>
            ) : null}
            <Modal
                open={showParticipantsModal}
                centered={true}
                footer={null}
                closable={false}
                onCancel={handleCloseParticipantsInfoPopup}
                width={500}
            >
                <ParticipantsInfoPopup patricipants={currentChatInfo} />
            </Modal>
        </div>
    );
};

export default ProspectMessagingHeader;
